import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Check, ImagePlus, Loader2, Search, UserPlus, X } from 'lucide-react'
import { authFetch, getLocalApiUrl } from '../../../config/api'
import { normalizePastedScreenshotFile } from './PasteScreenshotZone'

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

function base64ToImageFile(raw, fileName = 'face.jpg') {
  const cleaned = String(raw || '').trim()
  if (!cleaned) throw new Error('No image data')
  const dataUrl = cleaned.startsWith('data:') ? cleaned : `data:image/jpeg;base64,${cleaned}`
  const comma = dataUrl.indexOf(',')
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : cleaned
  const mime = dataUrl.match(/^data:([^;]+)/)?.[1] || 'image/jpeg'
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  const blob = new Blob([bytes], { type: mime })
  return new File([blob], fileName, { type: mime })
}

function displayNameFromCandidate(c) {
  const ud = c?.profilesafe_user_data || {}
  const patron = c?.patron
  const fn = patron?.first_name || ud.FirstName || ud.FN || ''
  const ln = patron?.last_name || ud.LastName || ud.LN || ''
  const name = `${fn} ${ln}`.trim()
  return name || 'Unknown person'
}

function matchBandLabel(band) {
  if (band === 'high') return 'Likely match'
  if (band === 'medium') return 'Possible match'
  return 'Low confidence'
}

function matchBandClass(band) {
  if (band === 'high') return 'bg-green-100 text-green-800 border-green-200'
  if (band === 'medium') return 'bg-amber-100 text-amber-900 border-amber-200'
  return 'bg-slate-100 text-slate-600 border-slate-200'
}

/**
 * Identify a face via ProfileSafe and optionally add as case suspect.
 * Used from Suspects tab (paste/upload), Evidence tab (preloaded still), and suspect VMS face.
 */
export default function FaceIdentifyModal({
  open,
  onClose,
  caseId,
  context = 'investigation',
  evidenceStepId = null,
  evidenceId = null,
  title = 'Identify from photo',
  subtitle = 'Paste or upload a face image to search enrolled ProfileSafe profiles.',
  preloadFromEvidence = null,
  preloadFromBase64 = null,
  onAddSuspect,
}) {
  const fileInputRef = useRef(null)
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [identifying, setIdentifying] = useState(false)
  const [candidates, setCandidates] = useState([])
  const [error, setError] = useState('')
  const [addingId, setAddingId] = useState(null)

  const reset = useCallback(() => {
    setImageFile(null)
    setImagePreview(null)
    setCandidates([])
    setError('')
    setIdentifying(false)
    setAddingId(null)
  }, [])

  useEffect(() => {
    if (!open) {
      reset()
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        if (preloadFromEvidence?.caseId && preloadFromEvidence?.stepId && preloadFromEvidence?.evidenceId) {
          setError('')
          const url = getLocalApiUrl(
            `/api/cases/${encodeURIComponent(preloadFromEvidence.caseId)}/steps/${encodeURIComponent(preloadFromEvidence.stepId)}/evidence-files/${encodeURIComponent(preloadFromEvidence.evidenceId)}?inline=1`
          )
          const res = await authFetch(url)
          if (!res.ok) throw new Error(`Could not load evidence image (HTTP ${res.status})`)
          const blob = await res.blob()
          const file = new File([blob], preloadFromEvidence.fileName || 'evidence.jpg', {
            type: blob.type || 'image/jpeg',
          })
          if (cancelled) return
          setImageFile(file)
          setImagePreview(URL.createObjectURL(blob))
          return
        }
        if (preloadFromBase64?.base64) {
          setError('')
          const file = base64ToImageFile(preloadFromBase64.base64, preloadFromBase64.fileName || 'vms-face.jpg')
          if (cancelled) return
          setImageFile(file)
          setImagePreview(URL.createObjectURL(file))
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load image')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, preloadFromEvidence, preloadFromBase64, reset])

  const setImage = (file) => {
    if (!file?.type?.startsWith('image/')) return
    const normalized = normalizePastedScreenshotFile(file)
    setImageFile(normalized)
    setImagePreview(URL.createObjectURL(normalized))
    setCandidates([])
    setError('')
  }

  const handlePaste = (e) => {
    const items = e.clipboardData?.items
    if (!items) return
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault()
        const file = item.getAsFile()
        if (file) setImage(file)
        break
      }
    }
  }

  const runIdentify = async () => {
    if (!imageFile || !caseId) return
    setIdentifying(true)
    setError('')
    setCandidates([])
    try {
      const dataUrl = await fileToDataUrl(imageFile)
      const base64 = String(dataUrl).includes(',') ? String(dataUrl).split(',')[1] : dataUrl
      const res = await authFetch(getLocalApiUrl('/api/profiles/face-identify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_base64: base64,
          context,
          case_id: caseId,
          smart_crop: true,
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) {
        throw new Error(json.error || `Identify failed (HTTP ${res.status})`)
      }
      setCandidates(json.data?.candidates || [])
    } catch (e) {
      setError(e.message || 'Face identify failed')
    } finally {
      setIdentifying(false)
    }
  }

  const handleAdd = async (candidate, asMember) => {
    if (!onAddSuspect) return
    const patron = candidate.patron
    const mid = patron?.member_id || candidate.profilesafe_user_data?.MemberID || null
    const displayName = displayNameFromCandidate(candidate)
    setAddingId(candidate.profilesafe_profile_id)
    try {
      const payload = {
        source: 'face_identify',
        face_match_source: 'face_identify',
        profilesafe_profile_id: candidate.profilesafe_profile_id,
        identification_confidence: candidate.identification_confidence,
        display_name: displayName,
      }
      if (evidenceId != null) payload.face_match_evidence_id = Number(evidenceId)
      if (asMember && mid) {
        payload.member_id = String(mid).trim()
        payload.subject_type = 'member'
      } else {
        payload.display_name = displayName
      }
      const ok = await onAddSuspect(payload)
      if (ok) onClose()
    } finally {
      setAddingId(null)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} role="presentation" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg border border-slate-200 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" aria-label="Close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {!imagePreview ? (
            <div
              tabIndex={0}
              onPaste={handlePaste}
              className="py-8 px-4 text-center border-2 border-dashed border-slate-300 rounded-lg outline-none focus:border-slate-400 cursor-text"
            >
              <ImagePlus className="mx-auto h-8 w-8 text-slate-400 mb-2" />
              <p className="text-sm text-slate-600">Paste screenshot (Ctrl+V / Cmd+V) or upload</p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 text-xs font-semibold"
              >
                Choose image
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) setImage(f)
                  e.target.value = ''
                }}
              />
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <img src={imagePreview} alt="Face preview" className="w-24 h-24 object-cover rounded-lg border border-slate-200" />
              <div className="flex-1 space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    setImageFile(null)
                    setImagePreview(null)
                    setCandidates([])
                  }}
                  className="text-xs text-slate-600 underline"
                >
                  Change image
                </button>
                <button
                  type="button"
                  disabled={identifying}
                  onClick={runIdentify}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                  style={{ background: '#0a5169' }}
                >
                  {identifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  {identifying ? 'Identifying…' : 'Identify face'}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
          )}

          {candidates.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Matches</div>
              {candidates.map((c) => {
                const name = displayNameFromCandidate(c)
                const mid = c.patron?.member_id || c.profilesafe_user_data?.MemberID
                const pct = Math.round((c.identification_confidence || 0) * 100)
                const busy = addingId === c.profilesafe_profile_id
                return (
                  <div key={c.profilesafe_profile_id} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {mid ? `Member ${mid}` : 'No local member ID'}
                          {' · '}
                          ProfileSafe {c.profilesafe_profile_id?.slice(0, 8)}…
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${matchBandClass(c.match_band)}`}>
                            {matchBandLabel(c.match_band)} · {pct}%
                          </span>
                          {!c.patron && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-50 text-amber-900 border border-amber-200">
                              Not in member DB
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {mid && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => handleAdd(c, true)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-semibold text-white disabled:opacity-50"
                          style={{ background: '#16a34a' }}
                        >
                          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          Add as member suspect
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => handleAdd(c, false)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-semibold border border-slate-300 bg-white disabled:opacity-50"
                      >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
                        {mid ? 'Add without member link' : 'Add as unknown suspect'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {!identifying && candidates.length === 0 && imagePreview && !error && (
            <p className="text-xs text-slate-500">
              Searches face-enrolled ProfileSafe profiles only. Confirm a suspect separately to set the case subject.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
